import { useState, useEffect, useCallback, useRef } from "react";
import { getRemainingTime } from "../../utils/newyear/eventDateUtils";
import { POLLING_INTERVAL } from "../../constants/newyearConstants";

const useCountdown = (deadline, options = {}) => {
  const { onExpire, enabled = true } = options;

  const [timeLeft, setTimeLeft] = useState(() => getRemainingTime(deadline));
  const intervalRef = useRef(null);
  const expiredRef = useRef(false);

  const updateTime = useCallback(() => {
    const remaining = getRemainingTime(deadline);
    setTimeLeft(remaining);

    if (remaining?.expired && !expiredRef.current) {
      expiredRef.current = true;
      onExpire?.();
    }
  }, [deadline, onExpire]);

  const start = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    expiredRef.current = false;
    updateTime();
    intervalRef.current = setInterval(updateTime, POLLING_INTERVAL.COUNTDOWN);
  }, [updateTime]);

  const stop = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const reset = useCallback(
    (newDeadline) => {
      expiredRef.current = false;
      if (newDeadline) {
        setTimeLeft(getRemainingTime(newDeadline));
      } else {
        setTimeLeft(getRemainingTime(deadline));
      }
    },
    [deadline]
  );

  useEffect(() => {
    if (enabled && deadline) {
      start();
    } else {
      stop();
    }

    return () => stop();
  }, [enabled, deadline, start, stop]);

  return {
    timeLeft,
    isExpired: timeLeft?.expired ?? false,
    display: timeLeft?.display ?? "00:00:00",
    hours: timeLeft?.hours ?? 0,
    minutes: timeLeft?.minutes ?? 0,
    seconds: timeLeft?.seconds ?? 0,
    totalSeconds: timeLeft?.totalSeconds ?? 0,
    start,
    stop,
    reset,
  };
};

export default useCountdown;
